import EventEmitter from '../Utils/EventEmitter.js'

export default class AudioAnalyser extends EventEmitter {
    constructor() {
        super()
        this.bass    = 0
        this.mid     = 0
        this.high    = 0
        this.isPlaying = false

        this._ctx        = null
        this._analyser   = null
        this._muteGain   = null
        this._scriptProc = null
        this._scriptRms  = 0
        this._source     = null
        this._dataArray  = null
        this._objectUrl  = null

        // Pre-create audio element so load() can set src without needing AudioContext.
        // createMediaElementSource() is deferred to prewarm() — it must run inside a
        // direct user gesture (click/touchstart) to satisfy Chrome's autoplay policy.
        // Chrome does NOT treat the file-input `change` event as a trusted activation.
        this._audioTag = new Audio()
        this._audioTag.onended = () => {
            this._audioTag.currentTime = 0
            this.isPlaying = false
            this.trigger('ended')
        }
    }

    // Call this inside a direct touchstart/click handler to unlock AudioContext.
    // iOS does NOT grant audio unlock from file-input change events.
    // Chrome does NOT grant AudioContext autoplay from file-input change events.
    prewarm() {
        if (!this._ctx) {
            this._ctx = new (window.AudioContext || window.webkitAudioContext)()

            this._analyser = this._ctx.createAnalyser()
            this._analyser.fftSize = 256
            this._analyser.smoothingTimeConstant = 0.8
            this._dataArray = new Uint8Array(this._analyser.frequencyBinCount)

            // <audio> tag → MediaElementSource classifies audio as Media Playback,
            // which bypasses the iOS Silent Switch (unlike BufferSource = Ambient Audio).
            // No crossOrigin: files are local blob URLs, CORS attribute blocks them in Safari.
            this._source = this._ctx.createMediaElementSource(this._audioTag)

            // Playback path: source → destination (Media Playback category, bypasses Silent Switch).
            this._source.connect(this._ctx.destination)

            // Analysis path: source → analyser → muteGain(0) → destination.
            // iOS WebKit only processes nodes that belong to a complete chain ending at
            // destination. A "dangling" analyser (no output) returns zeros from
            // getByteFrequencyData. The muted gain completes the chain without adding volume.
            this._muteGain = this._ctx.createGain()
            this._muteGain.gain.value = 0
            this._source.connect(this._analyser)
            this._analyser.connect(this._muteGain)
            this._muteGain.connect(this._ctx.destination)

            // ScriptProcessorNode fallback: iOS WebKit sometimes returns all zeros from
            // getByteFrequencyData() for MediaElementSource even with a complete chain.
            // ScriptProcessor.onaudioprocess fires with real PCM samples regardless,
            // giving us a reliable amplitude signal to fall back on.
            this._scriptProc = this._ctx.createScriptProcessor(2048, 1, 1)
            const _procMute  = this._ctx.createGain()
            _procMute.gain.value = 0
            this._source.connect(this._scriptProc)
            this._scriptProc.connect(_procMute)
            _procMute.connect(this._ctx.destination)

            this._scriptProc.onaudioprocess = (e) => {
                const buf = e.inputBuffer.getChannelData(0)
                let s = 0
                for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i]
                this._scriptRms = Math.sqrt(s / buf.length)
            }

            // PWA / iOS: AudioContext goes 'interrupted' when app loses audio focus
            // (screen lock, background, phone call). When it returns to 'running',
            // the audio element stays paused — we must restart it manually.
            this._ctx.addEventListener('statechange', () => {
                if (this._ctx.state === 'running' && this.isPlaying && this._audioTag.paused) {
                    this._audioTag.play().catch(e => console.warn('[AudioAnalyser] re-play after interrupt failed:', e))
                }
            })
        }
        if (this._ctx.state === 'suspended' || this._ctx.state === 'interrupted') this._ctx.resume()
    }

    load(file) {
        // No prewarm() here — AudioContext must be created from a direct user gesture,
        // not from the file-input change event (untrusted activation in Chrome).
        this._audioTag.pause()
        this.isPlaying = false

        if (this._objectUrl) URL.revokeObjectURL(this._objectUrl)
        this._objectUrl = URL.createObjectURL(file)
        this._audioTag.src = this._objectUrl

        this._audioTag.onloadedmetadata = () => {
            this.trigger('ready', [file.name])
        }
    }

    play() {
        if (!this._audioTag || !this._objectUrl || this.isPlaying) return
        // Fallback: if prewarm() wasn't called via touchstart/mousedown earlier,
        // create AudioContext here (play button click is always a trusted gesture).
        if (!this._ctx) this.prewarm()
        if (this._ctx.state === 'suspended' || this._ctx.state === 'interrupted') this._ctx.resume()
        this._audioTag.play().catch(e => console.error('[AudioAnalyser] play failed', e))
        this.isPlaying = true
    }

    pause() {
        if (!this._audioTag || !this.isPlaying) return
        this._audioTag.pause()
        this.isPlaying = false
    }

    toggle() {
        this.isPlaying ? this.pause() : this.play()
    }

    get audioLevel() {
        return (this.bass + this.mid + this.high) / 3
    }

    loadFile(file) {
        this.load(file)
    }

    get currentTime() {
        return this._audioTag ? this._audioTag.currentTime : 0
    }

    get duration() {
        return this._audioTag ? (this._audioTag.duration || 0) : 0
    }

    seek(t) {
        if (!this._audioTag) return
        this._audioTag.currentTime = Math.max(0, Math.min(t, this.duration - 0.001))
    }

    update() {
        if (!this._analyser || !this._audioTag || this._audioTag.paused) {
            this.bass = this.bass * 0.92
            this.mid  = this.mid  * 0.92
            this.high = this.high * 0.92

            return
        }

        // AudioContext.resume() is async — skip this frame if context not running yet.
        if (this._ctx.state !== 'running') return

        this._analyser.getByteFrequencyData(this._dataArray)
        const n = this._dataArray.length
        const b = Math.floor(n * 0.1)
        const m = Math.floor(n * 0.5)

        const bassRaw = this._avg(0, b) / 255
        const midRaw  = this._avg(b, m) / 255
        const highRaw = this._avg(m, n) / 255

        // iOS WebKit bug: getByteFrequencyData() returns all zeros for MediaElementSource
        // even when muteGain chain is in place. ScriptProcessorNode.onaudioprocess fires
        // with real PCM — use its RMS as fallback when analyser data is silent.
        if (bassRaw === 0 && midRaw === 0 && highRaw === 0 && this._scriptRms > 0.001) {
            const amp  = Math.min(1, this._scriptRms * 8)
            this.bass  = amp
            this.mid   = amp * 0.65
            this.high  = amp * 0.45
        } else {
            this.bass = bassRaw
            this.mid  = midRaw
            this.high = highRaw
        }

    }

    _avg(start, end) {
        let sum = 0
        for (let i = start; i < end; i++) sum += this._dataArray[i]
        return sum / (end - start)
    }
}
